import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MessageFormatter from '../MessageFormatter';
import SuggestionDialog from './SuggestionDialog';
import SkeletonShimmer from './SkeletonShimmer';
import { validateQuestion, analyzeQuestion } from '../../services/analyticsService';
import logo from '../../assets/images/Supervoima-Logo-White.svg';

/**
 * AskQuestionsTab - Interactive Q&A interface for analytics exploration
 *
 * Features:
 * - Natural language question input (max 400 chars)
 * - Real-time character counter
 * - Markdown-rendered answers
 * - Suggestion dialog for rejected queries
 * - Rate limit and error handling
 * - Cached answer indication
 *
 * @param {string} orgId - Organization ID to query
 */
const AskQuestionsTab = ({ orgId }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState('');

  // Three-phase loading states
  const [pendingQuestion, setPendingQuestion] = useState(null); // Step 1: Show immediately
  const [validating, setValidating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [guardInterpretation, setGuardInterpretation] = useState(null); // Step 2: Guard returns

  // Suggestion dialog state
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const MAX_CHARS = 400;
  const charCount = question.length;
  const isValid = question.trim().length > 0 && charCount <= MAX_CHARS;

  // Format timestamp to DD.MM.YYYY HH:MM format (matching GoalAlignedReportLayout)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const exampleQuestions = [
    // Capability & Training
    'How much are employees using AI tools right now and for what?',
    'What are our biggest AI capability gaps and in what departments?',
    'Can you identify any processes that could benefit from AI?',
    'What are the main fears or concerns people have about AI?',

    // Strategy & ROI
    'Which areas in AI should we prioritize for the quickest win?',
    'Is the main barrier to AI adoption technical or cultural?'

];

  const handleSubmit = async (questionText = question) => {
    if (!questionText.trim() || !orgId) return;

    // Reset state
    setError('');
    setAnswer(null);
    setGuardInterpretation(null);

    try {
      // ===== STEP 1: Show original question immediately =====
      setPendingQuestion(questionText);
      setValidating(true);
      const validation = await validateQuestion(questionText, orgId);

      if (!validation.valid) {
        // Question rejected - show suggestion dialog
        setValidationError({
          message: validation.rejection_reason,
          validation: {
            suggested_question: validation.suggested_question
          }
        });
        setShowSuggestion(true);
        setValidating(false);
        setPendingQuestion(null);
        return;
      }

      // Question validated - show Guard interpretation (store original for dual display)
      setGuardInterpretation({
        original_question: questionText,
        enhanced_question: validation.enhanced_question,
        category: validation.category
      });
      setValidating(false);

      // ===== PHASE 2: ANALYSIS =====
      setAnalyzing(true);
      const response = await analyzeQuestion(
        questionText,
        validation.enhanced_question,
        orgId,
        validation.category,
        validation.confidence
      );

      setAnswer(response);
      setQuestion(''); // Clear input on success
      setGuardInterpretation(null); // Clear loading states
      setPendingQuestion(null);
      setAnalyzing(false);

    } catch (err) {
      // Handle rate limit errors
      if (err.code === 'RATE_LIMIT_EXCEEDED') {
        const retryMsg = err.retry_after
          ? ` Please try again in ${err.retry_after} seconds.`
          : '';
        setError(err.message + retryMsg);
      }
      // Handle other errors
      else {
        setError(err.message || 'Failed to get answer. Please try again.');
      }

      setValidating(false);
      setAnalyzing(false);
      setGuardInterpretation(null);
      setPendingQuestion(null);
    }
  };

  const handleKeyPress = (event) => {
    // Submit on Enter (without Shift for multiline support)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (isValid && !validating && !analyzing) {
        handleSubmit();
      }
    }
  };

  const isLoading = validating || analyzing;

  const handleAcceptSuggestion = () => {
    const suggestedQuestion = validationError?.validation?.suggested_question;
    if (suggestedQuestion) {
      setShowSuggestion(false);
      setValidationError(null);
      handleSubmit(suggestedQuestion);
    }
  };

  const handleReviseSuggestion = () => {
    const suggestedQuestion = validationError?.validation?.suggested_question;
    if (suggestedQuestion) {
      setQuestion(suggestedQuestion);
    }
    setShowSuggestion(false);
    setValidationError(null);
  };

  const handleCancelSuggestion = () => {
    setShowSuggestion(false);
    setValidationError(null);
  };

  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto', p: 3 }}>
      {/* Input Section */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            backgroundColor: 'rgb(1, 28, 45)',
            color: 'white',
            px: 3,
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <img src={logo} alt="Supervoima Logo" style={{ height: '36px', width: 'auto', marginRight: '16px' }} />
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Bebas Neue',
              fontWeight: 'normal',
              textAlign: 'center',
              flexGrow: 1,
              mb: 0
            }}
          >
            ASK QUESTIONS ABOUT YOUR ASSESSMENT DATA (BETA)
          </Typography>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Ask natural language questions about your organization's AI capabilities,
            training needs, and assessment results. 
            Each question will generate a custom response based on your organisation's data only. 
          </Typography>
        </Box>

        <CardContent sx={{ pt: 1 }}>

          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="e.g., Which teams need the most training on AI tools?"
            value={question}
            onChange={(e) => setQuestion(e.target.value.slice(0, MAX_CHARS))}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            inputProps={{
              maxLength: MAX_CHARS,
              'aria-label': 'Ask analytics question'
            }}
            helperText={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Example: "{exampleQuestions[0]}"</span>
                <span style={{ color: charCount >= MAX_CHARS ? '#d32f2f' : 'inherit' }}>
                  {charCount}/{MAX_CHARS} characters
                </span>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="medium"
              startIcon={isLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SendIcon />}
              onClick={() => handleSubmit()}
              disabled={!isValid || isLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgb(1, 28, 45)',
                color: 'white',
                px: 3,
                py: 1,
                minWidth: '180px',
                borderRadius: '20px',
                '&:hover': {
                  backgroundColor: 'rgb(1, 40, 60)'
                },
                '&:disabled': {
                  backgroundColor: 'rgba(1, 28, 45, 0.5)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              {validating ? 'Validating...' : analyzing ? 'Analyzing...' : 'Ask'}
            </Button>
          </Box>

          {/* Example Questions */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Example questions:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {exampleQuestions.map((example, idx) => (
                <Chip
                  key={idx}
                  label={example}
                  size="small"
                  variant="outlined"
                  onClick={() => !isLoading && setQuestion(example)}
                  sx={{ cursor: isLoading ? 'default' : 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Unified Response Card - progressively fills in */}
      {(pendingQuestion || answer) && (
        <Card elevation={3} sx={{ borderRadius: 2 }}>
          <Box
            sx={{
              backgroundColor: 'rgb(1, 28, 45)',
              color: 'white',
              px: 3,
              py: 2,
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontFamily: 'Bebas Neue', letterSpacing: 1, mb: 0 }}
            >
              AI DATA ANALYST ANSWER
            </Typography>
            {answer?.cached && (
              <Chip
                label="Cached"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white'
                }}
              />
            )}
          </Box>

          <CardContent sx={{ pt: 3 }}>
            {/* Original Question - shown immediately on submit */}
            {(pendingQuestion || (answer?.question_enhanced !== answer?.question_original)) && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Your question:
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "{pendingQuestion || answer?.question_original}"
                </Typography>
              </Box>
            )}

            {/* Enhanced Question - appears when Guard returns */}
            {(guardInterpretation?.enhanced_question && guardInterpretation.enhanced_question !== pendingQuestion) && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, borderLeft: 3, borderColor: 'primary.main' }}>
                <Typography variant="caption" color="primary" display="block">
                  Interpreted by our AI Data Analyst as:
                </Typography>
                <Typography variant="body2">
                  "{guardInterpretation.enhanced_question}"
                </Typography>
              </Box>
            )}
            {(answer && answer.question_enhanced !== answer.question_original && !guardInterpretation) && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, borderLeft: 3, borderColor: 'primary.main' }}>
                <Typography variant="caption" color="primary" display="block">
                  Interpreted by our AI Data Analyst as:
                </Typography>
                <Typography variant="body2">
                  "{answer.question_enhanced}"
                </Typography>
              </Box>
            )}

            {/* Loading shimmer OR actual answer */}
            {(validating || analyzing) ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {validating ? 'Understanding your question...' : 'Analyzing your data...'}
                </Typography>
                <SkeletonShimmer lines={4} />
              </Box>
            ) : answer && (
              <>
                {/* Answer Content */}
                <Box sx={{
                  '& > *': { mb: 1 },
                  fontSize: '1rem',
                  lineHeight: 1.7
                }}>
                  <MessageFormatter content={answer.answer} />
                </Box>

                {/* Metadata */}
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Generated: {formatTimestamp(answer.timestamp)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Analysis was conducted using AI-driven synthesis methods; double check before applying any recommendations.
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Suggestion Dialog */}
      <SuggestionDialog
        open={showSuggestion}
        validation={validationError?.validation}
        onAccept={handleAcceptSuggestion}
        onRevise={handleReviseSuggestion}
        onCancel={handleCancelSuggestion}
      />
    </Box>
  );
};

export default AskQuestionsTab;
